import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Plus, Minus, ExternalLink, AlertCircle } from 'lucide-react';
import axios from 'axios';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState({
    quantity: 1,
    size: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    notes: ''
  });
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [showOrderForm, setShowOrderForm] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`/products/${id}`);
      setProduct(response.data);
      // Set default size if available
      if (response.data.sizes && response.data.sizes.length > 0) {
        setOrderData(prev => ({ ...prev, size: response.data.sizes[0] }));
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (delta) => {
    setOrderData(prev => ({
      ...prev,
      quantity: Math.max(1, Math.min(prev.quantity + delta, product.stock))
    }));
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    setOrderLoading(true);
    setOrderError('');

    try {
      const response = await axios.post('/orders', {
        product_id: product.id,
        ...orderData
      });

      setOrderSuccess(true);
      if (response.data.whatsapp_url) {
        setWhatsappUrl(response.data.whatsapp_url);
      }
      
      // Reset form
      setOrderData({
        quantity: 1,
        size: product.sizes?.[0] || '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        notes: ''
      });
      
      // Refresh product to update stock
      fetchProduct();
    } catch (error) {
      setOrderError(error.response?.data?.detail || 'Failed to place order. Please try again.');
    } finally {
      setOrderLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/shop" className="btn btn-primary">
            <ArrowLeft className="h-4 w-4" />
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="h-8 w-8 text-green-600" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Order Placed Successfully!
            </h2>
            
            <p className="text-lg text-gray-600 mb-8">
              Thank you for your order! We'll be in touch soon to confirm payment and delivery details.
            </p>

            {whatsappUrl && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  Instant Order Confirmation
                </h3>
                <p className="text-green-700 mb-4">
                  Click below to send your order details directly to our sales team via WhatsApp.
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary bg-green-600 hover:bg-green-700 border-green-600 mb-4"
                >
                  <ExternalLink className="h-5 w-5" />
                  Send Order via WhatsApp
                </a>
              </div>
            )}

            <div className="space-y-4 text-left mb-8">
              <h3 className="text-lg font-semibold text-gray-900">What happens next?</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="w-6 h-6 bg-black text-white rounded-full text-sm flex items-center justify-center mt-0.5 mr-3">1</span>
                  We'll contact you within 24 hours to confirm your order
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 bg-black text-white rounded-full text-sm flex items-center justify-center mt-0.5 mr-3">2</span>
                  Payment arrangements will be made (bank transfer or card payment link)
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 bg-black text-white rounded-full text-sm flex items-center justify-center mt-0.5 mr-3">3</span>
                  Your order will be processed and shipped within 3-5 working days
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/shop" className="btn btn-primary">
                Continue Shopping
              </Link>
              <button
                onClick={() => setOrderSuccess(false)}
                className="btn btn-secondary"
              >
                Order Another Item
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalPrice = product.price * orderData.quantity;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link 
            to="/shop" 
            className="inline-flex items-center text-gray-600 hover:text-black transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Shop
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              {product.images && product.images.length > 0 ? (
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-96 lg:h-[500px] object-cover"
                />
              ) : (
                <div className="w-full h-96 lg:h-[500px] bg-gray-200 flex items-center justify-center">
                  <ShoppingBag className="h-24 w-24 text-gray-400" />
                </div>
              )}
            </div>
            
            {product.images && product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-4 mt-4">
                {product.images.slice(1, 5).map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${product.name} ${index + 2}`}
                    className="w-full h-20 object-cover rounded-lg shadow cursor-pointer hover:opacity-75 transition-opacity"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex items-start justify-between mb-4">
                <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                  {product.category}
                </span>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold text-black">£{product.price.toFixed(2)}</span>
                <div className="mt-2">
                  {product.stock > 0 ? (
                    <span className="text-green-600 font-medium">
                      {product.stock <= 5 ? `Only ${product.stock} left in stock` : 'In stock'}
                    </span>
                  ) : (
                    <span className="text-red-600 font-medium">Out of stock</span>
                  )}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-3">Description</h2>
                <p className="text-gray-600 leading-relaxed">{product.description}</p>
              </div>

              {product.stock > 0 && (
                <>
                  {/* Size Selection */}
                  {product.sizes && product.sizes.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-3">Size</h3>
                      <div className="flex flex-wrap gap-3">
                        {product.sizes.map((size) => (
                          <button
                            key={size}
                            onClick={() => setOrderData(prev => ({ ...prev, size }))}
                            className={`px-4 py-2 border-2 rounded-lg font-medium transition-colors ${
                              orderData.size === size
                                ? 'border-black bg-black text-white'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quantity */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-3">Quantity</h3>
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleQuantityChange(-1)}
                        disabled={orderData.quantity <= 1}
                        className="w-10 h-10 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="text-xl font-semibold w-8 text-center">{orderData.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(1)}
                        disabled={orderData.quantity >= product.stock}
                        className="w-10 h-10 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Total:</span>
                      <span className="text-2xl font-bold">£{totalPrice.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Buy Now Button */}
                  <button
                    onClick={() => setShowOrderForm(true)}
                    className="w-full btn btn-primary text-lg py-4 mb-4"
                  >
                    <ShoppingBag className="h-5 w-5" />
                    Buy Now
                  </button>
                </>
              )}

              {product.stock === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Out of Stock</h3>
                  <p className="text-gray-600 mb-4">
                    This item is currently unavailable. Contact us for restock information.
                  </p>
                  <a 
                    href="mailto:sales@crawleyafc.uk" 
                    className="btn btn-secondary"
                  >
                    Contact Sales
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Form Modal */}
        {showOrderForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Complete Your Order</h2>
                  <button
                    onClick={() => setShowOrderForm(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>

                {orderError && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{orderError}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleOrder} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label htmlFor="customer_name" className="form-label">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="customer_name"
                        name="customer_name"
                        required
                        className="form-input"
                        value={orderData.customer_name}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="customer_phone" className="form-label">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="customer_phone"
                        name="customer_phone"
                        required
                        className="form-input"
                        value={orderData.customer_phone}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="customer_email" className="form-label">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="customer_email"
                      name="customer_email"
                      required
                      className="form-input"
                      value={orderData.customer_email}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="customer_address" className="form-label">
                      Delivery Address (Optional)
                    </label>
                    <textarea
                      id="customer_address"
                      name="customer_address"
                      rows={3}
                      className="form-textarea"
                      placeholder="Full delivery address if you need shipping"
                      value={orderData.customer_address}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes" className="form-label">
                      Special Instructions (Optional)
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={3}
                      className="form-textarea"
                      placeholder="Any special requests or notes for your order"
                      value={orderData.notes}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Order Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold mb-2">Order Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Product:</span>
                        <span>{product.name}</span>
                      </div>
                      {orderData.size && (
                        <div className="flex justify-between">
                          <span>Size:</span>
                          <span>{orderData.size}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Quantity:</span>
                        <span>{orderData.quantity}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-lg border-t pt-2">
                        <span>Total:</span>
                        <span>£{totalPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      type="submit"
                      disabled={orderLoading}
                      className={`flex-1 btn btn-primary ${orderLoading ? 'loading' : ''}`}
                    >
                      {orderLoading ? 'Placing Order...' : 'Place Order'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowOrderForm(false)}
                      className="flex-1 btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;